import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import Table, { TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/Table';
import { LocalLinkedProgram } from './types';

type SortDirection = 'asc' | 'desc';

interface LinkedProgramsTableProps {
  disabled?: boolean;
  onRemoveStaged: (programId: number) => void;
  onToggleUnlink: (programId: number) => void;
  programs: LocalLinkedProgram[];
  programsToUnlink: Set<number>;
}

const BORDER = 'tw-border-solid tw-border-[1px] tw-border-med-grey';
const HEADER_CELL = clsx(BORDER, 'tw-text-left tw-font-semibold');

const LinkedProgramsTable = ({
  disabled = false,
  programs,
  programsToUnlink,
  onRemoveStaged,
  onToggleUnlink,
}: LinkedProgramsTableProps) => {
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const toggleSort = () => {
    setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const sortedPrograms = useMemo(
    () => [...programs].sort((a, b) => {
      const cmp = a.name.localeCompare(b.name);
      return sortDirection === 'asc' ? cmp : -cmp;
    }),
    [programs, sortDirection],
  );

  const isRemoveEnabled = (program: LocalLinkedProgram) => program.class_par_exists || program.staged;

  const handleCheckboxChange = (program: LocalLinkedProgram) => {
    if (program.staged) {
      onRemoveStaged(program.id);
    } else if (program.class_par_exists) {
      onToggleUnlink(program.id);
    }
  };

  return (
    <Table className="tw-w-full tw-border-collapse tw-mt-4">
      <TableHeader className="tw-bg-light-grey">
        <TableRow>
          <TableCell
            header
            className={clsx(HEADER_CELL, 'tw-w-[63%] tw-cursor-pointer tw-select-none')}
            onClick={toggleSort}
          >
            Name
            {' '}
            <i
              aria-hidden
              className={clsx('fa', {
                'fa-caret-down': sortDirection === 'desc',
                'fa-caret-up': sortDirection === 'asc',
              })}
            />
          </TableCell>
          <TableCell header className={clsx(HEADER_CELL, 'tw-w-[22%]')}>
            Class PAR exists?
          </TableCell>
          <TableCell header className={clsx(HEADER_CELL, 'tw-w-[15%] tw-text-center')}>
            Remove
          </TableCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedPrograms.length === 0 && (
          <TableRow>
            <TableCell className={BORDER} colSpan={3}>
              No Programs linked to this class
            </TableCell>
          </TableRow>
        )}
        {sortedPrograms.map(program => (
          <TableRow key={program.id}>
            <TableCell className={BORDER}>
              {program.name}
              {program.staged && (
                <span className="tw-text-green tw-text-sm tw-ml-2">(new)</span>
              )}
            </TableCell>
            <TableCell className={BORDER}>
              {program.class_par_exists ? 'Y' : 'N'}
            </TableCell>
            <TableCell className={clsx(BORDER, 'tw-text-center')}>
              <input
                checked={programsToUnlink.has(program.id)}
                disabled={disabled || !isRemoveEnabled(program)}
                type="checkbox"
                onChange={() => handleCheckboxChange(program)}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default LinkedProgramsTable;
